import { useEffect, useRef, useState, type Key } from 'react';
import { Link } from 'react-router-dom';
import {
	Alert,
	Button,
	Card,
	Checkbox,
	Collapse,
	DatePicker,
	Empty,
	Flex,
	Input,
	Modal,
	Segmented,
	Select,
	Space,
	Spin,
	Table,
	Tabs,
	Tag,
	Typography,
	Upload,
	message,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
	BarChartOutlined,
	CloseCircleOutlined,
	DeleteOutlined,
	DownOutlined,
	InboxOutlined,
	DownloadOutlined,
	ReloadOutlined,
	RightOutlined,
	SearchOutlined,
	ThunderboltOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	LabelList,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
} from 'recharts';
import {
	AuditDocumentListItem,
	AuditRun,
	AuditRunListItem,
	AuditStatus,
	DocumentOrdering,
	ErpRunCriteria,
	ErpRunPopulation,
	FaiMeasurement,
	IssueBreakdown,
	TravelerData,
	useCreateAuditRunMutation,
	useCreateErpRunMutation,
	useDeleteAuditRunMutation,
	useGetAuditDocumentQuery,
	useGetAuditRunDocumentsQuery,
	useGetIssueBreakdownQuery,
	useGetAuditRunsQuery,
	useLazyExportAuditRunQuery,
	useLazyGetAuditRunQuery,
} from '../../redux/api/traveler-audit';
import { groupIssues, type IssueCluster } from './groupIssues';
import { OverlayDates, ReviewPanel } from './ReviewPanel';
import { PrintQueue } from './PrintQueue';
import { RecoveryAudit } from './RecoveryAudit';

const { Dragger } = Upload;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

type AuditMode = 'erp' | 'upload';

/** Soft warning while a large Live ERP run is still processing. */
const ERP_POLL_WARN_MS = 3 * 60 * 1000;
/** Absolute cap so a stuck pending run (no worker) fails loudly. */
const ERP_POLL_TIMEOUT_MS = 30 * 60 * 1000;
/** Poll cadence: start snappy, then back off so long runs stay cheap. */
const ERP_POLL_INTERVAL_FAST_MS = 2500;
const ERP_POLL_INTERVAL_MED_MS = 5000;
const ERP_POLL_INTERVAL_SLOW_MS = 10000;
/** Order Accepted date window ceiling; mirrors the backend MAX_ERP_RANGE_DAYS. */
const MAX_RANGE_DAYS = 366;
const DEFAULT_RANGE_DAYS = 30;
const PAGE_SIZE_OPTIONS = ['25', '50', '100', '200'];

const STATUS_COLOR: Record<AuditStatus, string> = {
	pass: 'green',
	review: 'gold',
	fail: 'red',
	complete: 'blue',
	printed: 'purple',
	internal: 'default',
};

const toNumber = (value: unknown): number | null => {
	const n = parseFloat(String(value));
	return Number.isFinite(n) ? n : null;
};

/** A single FAI sample's in-tolerance verdict for the table. */
const sampleVerdict = (m: FaiMeasurement): 'ok' | 'out' | 'n/a' => {
	if (m.visual) {
		const allPass = m.samples.every((s) => String(s).trim().toLowerCase() === 'pass');
		return m.samples.length ? (allPass ? 'ok' : 'out') : 'n/a';
	}
	const lcl = toNumber(m.lcl);
	const ucl = toNumber(m.ucl);
	if (lcl === null || ucl === null || !m.samples.length) return 'n/a';
	const allIn = m.samples.every((s) => {
		const v = toNumber(s);
		return v !== null && v >= lcl && v <= ucl;
	});
	return allIn ? 'ok' : 'out';
};

/** Order Accepted date window (and optional method) used for a Live ERP history row. */
const formatHistoryDateRange = (row: AuditRunListItem): string => {
	if (row.source !== 'erp') return '—';
	// Prefer the new keys; fall back so older history rows still render.
	const from = row.criteria?.accepted_date_from ?? row.criteria?.ship_date_from;
	const to = row.criteria?.accepted_date_to ?? row.criteria?.ship_date_to;
	if (!from && !to) return '—';
	const range = from && to ? `${from} → ${to}` : from || to || '—';
	const method = row.criteria?.production_method?.trim();
	return method ? `${range} (${method})` : range;
};

const POPULATION_LABELS: Record<string, string> = {
	awaiting_review: 'Awaiting review',
	completed: 'Already completed',
	both: 'Both',
};

/**
 * Which jobs a Live ERP history row swept. Without it a past run's document set
 * cannot be interpreted after the fact — the same window returns different jobs
 * per population. Runs recorded before this was a criterion carry none, and
 * those swept Completed, which is the historical default.
 */
const formatHistoryPopulation = (row: AuditRunListItem): string => {
	if (row.source !== 'erp') return '—';
	const population = row.criteria?.population ?? 'completed';
	return POPULATION_LABELS[population] ?? population;
};

/** Deep-link Work Order / Part # when the audit row is ERP-backed. */
const entityLink = (label: string | null | undefined, to: string | null) => {
	if (!label) return '-';
	if (!to) return label;
	return (
		<Link to={to} target="_blank" rel="noopener noreferrer">
			{label}
		</Link>
	);
};

const workOrderLink = (d: AuditDocumentListItem) =>
	entityLink(d.header.work_order, d.order ? `/orders/${d.order}` : null);

const partNumberLink = (d: AuditDocumentListItem) =>
	entityLink(
		d.header.part_number,
		d.order_line_item ? `/orders/line-items/${d.order_line_item}` : null
	);

const downloadBlob = (blob: Blob, filename: string) => {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const StatusTag = ({ status }: { status: AuditStatus }) => (
	<Tag color={STATUS_COLOR[status]}>{status.toUpperCase()}</Tag>
);

type IssuesTab = 'failures' | 'lookouts';

type IssuesModalTarget = {
	doc: AuditDocumentListItem;
	tab: IssuesTab;
};

/** Clickable count tag that opens the issues modal on the matching tab. */
const issueCountCell = (count: number, color: 'volcano' | 'gold', onClick: () => void) => {
	if (!count) return <Text type="secondary">-</Text>;
	return (
		<Tag color={color} style={{ cursor: 'pointer', userSelect: 'none' }} onClick={onClick}>
			{count}
		</Tag>
	);
};

const ISSUE_THEME = {
	failures: {
		accent: '#cf1322',
		bg: '#fff1f0',
		border: '#ffa39e',
		badgeBg: '#cf1322',
		empty: 'No failures on this item',
	},
	lookouts: {
		accent: '#d48806',
		bg: '#fffbe6',
		border: '#ffe58f',
		badgeBg: '#d48806',
		empty: 'No lookouts on this item',
	},
} as const;

type IssueTheme = (typeof ISSUE_THEME)[IssuesTab];

/** One numbered issue card — the shop-floor look, reused inside clusters. */
const IssueCard = ({
	text,
	badge,
	theme,
}: {
	text: string;
	badge: React.ReactNode;
	theme: IssueTheme;
}) => (
	<div
		style={{
			display: 'flex',
			alignItems: 'flex-start',
			gap: 10,
			padding: '10px 12px',
			background: theme.bg,
			border: `1px solid ${theme.border}`,
			borderLeft: `4px solid ${theme.accent}`,
			borderRadius: 6,
		}}
	>
		<span
			style={{
				flexShrink: 0,
				minWidth: 26,
				height: 26,
				padding: '0 6px',
				borderRadius: 6,
				background: theme.badgeBg,
				color: '#fff',
				fontWeight: 700,
				fontSize: 13,
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			{badge}
		</span>
		<span style={{ fontSize: 14, lineHeight: 1.4, color: 'rgba(0,0,0,0.88)' }}>{text}</span>
	</div>
);

/**
 * A cluster of near-identical messages. One-offs render as a plain card; a run
 * of similar messages collapses to a single row with a `× N` badge that expands
 * to the individual instances.
 */
const ClusterBlock = ({ cluster, theme }: { cluster: IssueCluster; theme: IssueTheme }) => {
	const [open, setOpen] = useState(false);
	if (cluster.items.length === 1) {
		return <IssueCard text={cluster.items[0]} badge={1} theme={theme} />;
	}
	return (
		<div
			style={{
				background: theme.bg,
				border: `1px solid ${theme.border}`,
				borderLeft: `4px solid ${theme.accent}`,
				borderRadius: 6,
			}}
		>
			<div
				role="button"
				onClick={() => setOpen((o) => !o)}
				style={{
					display: 'flex',
					alignItems: 'flex-start',
					gap: 10,
					padding: '10px 12px',
					cursor: 'pointer',
					userSelect: 'none',
				}}
			>
				<span
					style={{
						flexShrink: 0,
						minWidth: 34,
						height: 26,
						padding: '0 6px',
						borderRadius: 6,
						background: theme.badgeBg,
						color: '#fff',
						fontWeight: 700,
						fontSize: 13,
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					{`\u00D7 ${cluster.items.length}`}
				</span>
				<span style={{ flex: 1, fontSize: 14, lineHeight: 1.4, color: 'rgba(0,0,0,0.88)' }}>
					{cluster.sample}
				</span>
				{open ? (
					<DownOutlined style={{ color: theme.accent, marginTop: 4 }} />
				) : (
					<RightOutlined style={{ color: theme.accent, marginTop: 4 }} />
				)}
			</div>
			{open && (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 6,
						padding: '0 12px 12px 46px',
					}}
				>
					{cluster.items.map((item, i) => (
						<div
							key={i}
							style={{
								display: 'flex',
								gap: 8,
								fontSize: 13,
								lineHeight: 1.4,
								color: 'rgba(0,0,0,0.75)',
							}}
						>
							<span style={{ color: theme.accent, fontWeight: 700 }}>{i + 1}.</span>
							<span>{item}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

/** Issues grouped by category, then clustered by similar message text. */
const IssueList = ({ items, kind }: { items: string[]; kind: IssuesTab }) => {
	const theme = ISSUE_THEME[kind];
	if (!items.length) {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={theme.empty}
				style={{ padding: '20px 0' }}
			/>
		);
	}
	const groups = groupIssues(items);
	// Auto-expand small categories; keep noisy ones collapsed to reduce clutter.
	const defaultActive = groups.filter((g) => g.count <= 3).map((g) => g.key);
	return (
		<div style={{ maxHeight: 380, overflowY: 'auto' }}>
			<Collapse
				ghost
				defaultActiveKey={defaultActive}
				items={groups.map((g) => ({
					key: g.key,
					label: (
						<span style={{ fontWeight: 600 }}>
							{g.label}{' '}
							<Tag
								style={{
									marginInlineStart: 4,
									background: theme.badgeBg,
									color: '#fff',
									border: 'none',
								}}
							>
								{g.count}
							</Tag>
						</span>
					),
					children: (
						<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
							{g.clusters.map((c, i) => (
								<ClusterBlock key={i} cluster={c} theme={theme} />
							))}
						</div>
					),
				}))}
			/>
		</div>
	);
};

const HeaderField = ({ label, value }: { label: string; value: React.ReactNode }) => (
	<div style={{ minWidth: 0 }}>
		<div
			style={{
				fontSize: 10,
				fontWeight: 700,
				letterSpacing: 0.5,
				textTransform: 'uppercase',
				color: 'rgba(0,0,0,0.45)',
				marginBottom: 2,
			}}
		>
			{label}
		</div>
		<div
			style={{
				fontSize: 14,
				fontWeight: 600,
				lineHeight: 1.3,
				color: 'rgba(0,0,0,0.88)',
				wordBreak: 'break-word',
			}}
		>
			{value ?? <Text type="secondary">—</Text>}
		</div>
	</div>
);

/** Modal with Failures / Lookouts tabs; open on the tab that was clicked. */
function IssuesModal({
	target,
	onTabChange,
	onClose,
}: {
	target: IssuesModalTarget | null;
	onTabChange: (tab: IssuesTab) => void;
	onClose: () => void;
}) {
	const doc = target?.doc;
	const failCount = doc?.failures.length ?? 0;
	const lookoutCount = doc?.lookouts.length ?? 0;

	return (
		<Modal
			open={!!target}
			onCancel={onClose}
			title={<span style={{ fontSize: 16, fontWeight: 700 }}>Review issues</span>}
			footer={
				<Button type="primary" onClick={onClose}>
					Close
				</Button>
			}
			width={640}
			destroyOnClose
			styles={{ body: { paddingTop: 8 } }}
		>
			{doc && (
				<>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
							gap: 12,
							padding: '10px 12px',
							marginBottom: 12,
							background: '#fafafa',
							border: '1px solid #f0f0f0',
							borderRadius: 6,
						}}
					>
						<HeaderField label="Work order" value={workOrderLink(doc)} />
						<HeaderField label="Part #" value={partNumberLink(doc)} />
						<HeaderField label="Rev" value={doc.header.revision} />
						<HeaderField label="Qty" value={doc.header.quantity} />
						<HeaderField label="Due" value={doc.header.due_date} />
						<HeaderField label="Status" value={<StatusTag status={doc.status} />} />
					</div>

					{doc.filename ? (
						<Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
							File: {doc.filename}
						</Text>
					) : null}

					<Tabs
						activeKey={target?.tab}
						onChange={(key) => onTabChange(key as IssuesTab)}
						items={[
							{
								key: 'failures',
								label: (
									<span style={{ fontWeight: 600 }}>
										<CloseCircleOutlined style={{ marginRight: 6, color: '#cf1322' }} />
										Failures{' '}
										<Tag color="volcano" style={{ marginInlineStart: 4 }}>
											{failCount}
										</Tag>
									</span>
								),
								children: <IssueList items={doc.failures} kind="failures" />,
							},
							{
								key: 'lookouts',
								label: (
									<span style={{ fontWeight: 600 }}>
										<WarningOutlined style={{ marginRight: 6, color: '#d48806' }} />
										Lookouts{' '}
										<Tag color="gold" style={{ marginInlineStart: 4 }}>
											{lookoutCount}
										</Tag>
									</span>
								),
								children: <IssueList items={doc.lookouts} kind="lookouts" />,
							},
						]}
					/>
				</>
			)}
		</Modal>
	);
}

const REPORT_FAIL_COLOR = '#cf1322';
const REPORT_LOOKOUT_COLOR = '#d48806';
const REPORT_DOCS_COLOR = '#1677ff';
const REPORT_MIXED_COLOR = '#722ed1';

type ReportSeverityFilter = 'all' | 'failures' | 'lookouts';

type ReportRow = {
	key: string;
	label: string;
	docs: number;
	failures: number;
	lookouts: number;
	/** Share of docs-with-issues that hit this category. */
	sharePct: number;
	/** Dominant severity for bar coloring in "all" mode. */
	tone: 'fail' | 'lookout' | 'mixed';
};

const buildReportRows = (
	breakdown: IssueBreakdown,
	severity: ReportSeverityFilter
): ReportRow[] => {
	const docsWithIssues = Math.max(1, breakdown.totals.documents_with_issues);
	return breakdown.breakdown
		.filter((b) => {
			if (b.count <= 0) return false;
			if (severity === 'failures') return b.failure_count > 0;
			if (severity === 'lookouts') return b.lookout_count > 0;
			return true;
		})
		.map((b) => {
			const hasFail = b.failure_count > 0;
			const hasLookout = b.lookout_count > 0;
			const tone: ReportRow['tone'] =
				hasFail && hasLookout ? 'mixed' : hasFail ? 'fail' : 'lookout';
			return {
				key: b.key,
				label: b.label,
				docs: b.count,
				failures: b.failure_count,
				lookouts: b.lookout_count,
				sharePct: Math.round((b.count / docsWithIssues) * 1000) / 10,
				tone,
			};
		})
		.sort((a, b) => b.docs - a.docs || a.label.localeCompare(b.label));
};

const barColorForRow = (row: ReportRow, severity: ReportSeverityFilter) => {
	if (severity === 'failures') return REPORT_FAIL_COLOR;
	if (severity === 'lookouts') return REPORT_LOOKOUT_COLOR;
	if (row.tone === 'fail') return REPORT_FAIL_COLOR;
	if (row.tone === 'lookout') return REPORT_LOOKOUT_COLOR;
	return REPORT_MIXED_COLOR;
};

/** Compact entry point — totals + open the full chart modal. */
const IssueReportTrigger = ({
	breakdown,
	onOpen,
}: {
	breakdown: IssueBreakdown;
	onOpen: () => void;
}) => {
	const { failures, lookouts, documents_with_issues } = breakdown.totals;
	if (!failures && !lookouts) return null;
	const top = buildReportRows(breakdown, 'all')[0];
	return (
		<button
			type="button"
			onClick={onOpen}
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: 16,
				width: '100%',
				marginTop: 12,
				padding: '12px 16px',
				border: '1px solid #f0f0f0',
				borderRadius: 10,
				background:
					'linear-gradient(90deg, #fff1f0 0%, #fffbe6 55%, #fafafa 100%)',
				cursor: 'pointer',
				textAlign: 'left',
			}}
		>
			<Flex align="center" gap={12} wrap="wrap">
				<span
					style={{
						width: 36,
						height: 36,
						borderRadius: 8,
						background: '#fff',
						border: '1px solid #f0f0f0',
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: REPORT_FAIL_COLOR,
						fontSize: 18,
					}}
				>
					<BarChartOutlined />
				</span>
				<div>
					<div style={{ fontWeight: 700, fontSize: 14, color: 'rgba(0,0,0,0.88)' }}>
						Error report
					</div>
					<Text type="secondary" style={{ fontSize: 12 }}>
						{documents_with_issues} docs with issues
						{top ? ` · most common: ${top.label} (${top.docs} docs)` : ''}
						{' · '}
						{failures} fail / {lookouts} lookout
					</Text>
				</div>
			</Flex>
			<Tag color="blue" style={{ margin: 0 }}>
				View by type
			</Tag>
		</button>
	);
};

const ReportStat = ({
	label,
	value,
	color,
	soft,
	active,
	onClick,
}: {
	label: string;
	value: number;
	color: string;
	soft: string;
	active?: boolean;
	onClick?: () => void;
}) => (
	<button
		type="button"
		onClick={onClick}
		style={{
			flex: 1,
			minWidth: 120,
			padding: '12px 14px',
			borderRadius: 10,
			background: soft,
			border: active ? `2px solid ${color}` : `1px solid ${color}33`,
			cursor: onClick ? 'pointer' : 'default',
			textAlign: 'left',
			boxShadow: active ? `0 0 0 2px ${color}22` : 'none',
		}}
	>
		<div
			style={{
				fontSize: 11,
				fontWeight: 700,
				letterSpacing: 0.4,
				textTransform: 'uppercase',
				color,
				marginBottom: 4,
			}}
		>
			{label}
		</div>
		<div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1, color }}>
			{value.toLocaleString()}
		</div>
	</button>
);

/** Docs with issues per category type, filterable by failures vs lookouts. */
function IssueReportModal({
	open,
	breakdown,
	onClose,
	onSelectCategory,
}: {
	open: boolean;
	breakdown: IssueBreakdown | undefined;
	onClose: () => void;
	onSelectCategory?: (key: string) => void;
}) {
	const [severity, setSeverity] = useState<ReportSeverityFilter>('all');
	const data = breakdown ? buildReportRows(breakdown, severity) : [];
	const totals = breakdown?.totals;
	const top = data[0];
	const chartHeight = Math.max(280, data.length * 40 + 48);

	const insight = (() => {
		if (!top || !totals) return null;
		if (severity === 'failures') {
			return `${top.label} is the most widespread failure type — on ${top.docs} docs (${top.failures} failure instance${top.failures === 1 ? '' : 's'}).`;
		}
		if (severity === 'lookouts') {
			return `${top.label} is the most widespread lookout — on ${top.docs} docs (${top.lookouts} lookout instance${top.lookouts === 1 ? '' : 's'}).`;
		}
		return `${top.label} shows up on ${top.docs} of ${totals.documents_with_issues} docs with issues (${top.sharePct}%).`;
	})();

	return (
		<Modal
			open={open}
			onCancel={onClose}
			afterClose={() => setSeverity('all')}
			title={
				<span style={{ fontSize: 16, fontWeight: 700 }}>
					<BarChartOutlined style={{ marginRight: 8, color: REPORT_DOCS_COLOR }} />
					Docs with issues by type
				</span>
			}
			footer={
				<Button type="primary" onClick={onClose}>
					Close
				</Button>
			}
			width={880}
			centered
			destroyOnClose
			styles={{
				body: {
					paddingTop: 8,
					maxHeight: 'min(78vh, 820px)',
					overflowY: 'auto',
				},
			}}
		>
			{totals && (
				<Flex gap={12} wrap="wrap" style={{ marginBottom: 12 }}>
					<ReportStat
						label="Docs with issues"
						value={totals.documents_with_issues}
						color={REPORT_DOCS_COLOR}
						soft="#e6f4ff"
						active={severity === 'all'}
						onClick={() => setSeverity('all')}
					/>
					<ReportStat
						label="Failures"
						value={totals.failures}
						color={REPORT_FAIL_COLOR}
						soft="#fff1f0"
						active={severity === 'failures'}
						onClick={() => setSeverity('failures')}
					/>
					<ReportStat
						label="Lookouts"
						value={totals.lookouts}
						color={REPORT_LOOKOUT_COLOR}
						soft="#fffbe6"
						active={severity === 'lookouts'}
						onClick={() => setSeverity('lookouts')}
					/>
				</Flex>
			)}

			<Flex justify="space-between" align="center" wrap="wrap" gap="small" style={{ marginBottom: 12 }}>
				<Segmented<ReportSeverityFilter>
					value={severity}
					onChange={setSeverity}
					options={[
						{ label: 'All types', value: 'all' },
						{ label: 'Failures', value: 'failures' },
						{ label: 'Lookouts', value: 'lookouts' },
					]}
				/>
				<Space size={10} wrap style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)' }}>
					<span>
						<span
							style={{
								display: 'inline-block',
								width: 8,
								height: 8,
								borderRadius: 2,
								background: REPORT_FAIL_COLOR,
								marginRight: 4,
							}}
						/>
						Mostly failures
					</span>
					<span>
						<span
							style={{
								display: 'inline-block',
								width: 8,
								height: 8,
								borderRadius: 2,
								background: REPORT_LOOKOUT_COLOR,
								marginRight: 4,
							}}
						/>
						Mostly lookouts
					</span>
					<span>
						<span
							style={{
								display: 'inline-block',
								width: 8,
								height: 8,
								borderRadius: 2,
								background: REPORT_MIXED_COLOR,
								marginRight: 4,
							}}
						/>
						Both
					</span>
				</Space>
			</Flex>

			{insight && (
				<Alert type="info" showIcon style={{ marginBottom: 14 }} message={insight} />
			)}

			{!data.length ? (
				<Empty
					description={
						severity === 'failures'
							? 'No failure types in this run'
							: severity === 'lookouts'
								? 'No lookout types in this run'
								: 'No categorized issues in this run'
					}
				/>
			) : (
				<>
					<Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
						Bars = documents that hit each type. Hover for failure vs lookout
						instance counts. Click a bar to filter the table.
					</Text>
					<div
						style={{
							padding: '16px 12px 8px',
							borderRadius: 12,
							background: '#fafafa',
							border: '1px solid #f0f0f0',
						}}
					>
						<ResponsiveContainer width="100%" height={chartHeight}>
							<BarChart
								data={data}
								layout="vertical"
								margin={{ top: 4, right: 56, bottom: 8, left: 8 }}
								barCategoryGap="28%"
							>
								<CartesianGrid
									strokeDasharray="3 3"
									horizontal={false}
									stroke="#e8e8e8"
								/>
								<XAxis
									type="number"
									allowDecimals={false}
									tick={{ fontSize: 12, fill: 'rgba(0,0,0,0.45)' }}
									axisLine={false}
									tickLine={false}
								/>
								<YAxis
									type="category"
									dataKey="label"
									width={178}
									tick={{ fontSize: 12, fill: 'rgba(0,0,0,0.75)', fontWeight: 500 }}
									axisLine={false}
									tickLine={false}
								/>
								<RechartsTooltip
									cursor={{ fill: 'rgba(0,0,0,0.04)' }}
									contentStyle={{
										borderRadius: 8,
										border: '1px solid #f0f0f0',
										boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
									}}
									content={({ active, payload }) => {
										if (!active || !payload?.length) return null;
										const row = payload[0]?.payload as ReportRow | undefined;
										if (!row) return null;
										return (
											<div
												style={{
													background: '#fff',
													border: '1px solid #f0f0f0',
													borderRadius: 8,
													padding: '10px 12px',
													boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
													minWidth: 200,
												}}
											>
												<div style={{ fontWeight: 700, marginBottom: 6 }}>
													{row.label}
												</div>
												<div style={{ fontSize: 13, lineHeight: 1.55 }}>
													<div>
														<strong>{row.docs}</strong> docs with this type
														{' '}
														<span style={{ color: 'rgba(0,0,0,0.45)' }}>
															({row.sharePct}% of docs with issues)
														</span>
													</div>
													<div style={{ color: REPORT_FAIL_COLOR }}>
														{row.failures} failure instance
														{row.failures === 1 ? '' : 's'}
													</div>
													<div style={{ color: REPORT_LOOKOUT_COLOR }}>
														{row.lookouts} lookout instance
														{row.lookouts === 1 ? '' : 's'}
													</div>
												</div>
											</div>
										);
									}}
								/>
								<Bar
									dataKey="docs"
									name="docs"
									radius={[0, 6, 6, 0]}
									barSize={18}
									cursor={onSelectCategory ? 'pointer' : 'default'}
									onClick={(entry) => {
										const key = (entry as ReportRow | undefined)?.key;
										if (key) onSelectCategory?.(key);
									}}
								>
									{data.map((row) => (
										<Cell
											key={row.key}
											fill={barColorForRow(row, severity)}
										/>
									))}
									<LabelList
										dataKey="docs"
										position="right"
										style={{
											fill: 'rgba(0,0,0,0.55)',
											fontSize: 12,
											fontWeight: 600,
										}}
									/>
								</Bar>
							</BarChart>
						</ResponsiveContainer>
					</div>
				</>
			)}
		</Modal>
	);
}

/** Translate an Ant Table sorter change into a server ordering token. */
const sorterToOrdering = (
	sorter: SorterResult<AuditDocumentListItem> | SorterResult<AuditDocumentListItem>[]
): DocumentOrdering => {
	const s = Array.isArray(sorter) ? sorter[0] : sorter;
	if (!s || !s.order) return 'severity';
	if (s.columnKey === 'filename') return s.order === 'ascend' ? 'filename' : '-filename';
	// status column: ascending surfaces the worst (fail) first.
	return s.order === 'ascend' ? 'severity' : '-severity';
};

const faiMeasurementColumns: ColumnsType<FaiMeasurement> = [
	{ title: 'Balloon', render: (_v, m) => m.id, width: 80 },
	{ title: 'Nominal', render: (_v, m) => m.nominal ?? '-' },
	{ title: 'Minus', render: (_v, m) => m.minus ?? '-' },
	{ title: 'Plus', render: (_v, m) => m.plus ?? '-' },
	{ title: 'LCL', render: (_v, m) => m.lcl ?? '-' },
	{ title: 'UCL', render: (_v, m) => m.ucl ?? '-' },
	{ title: 'Samples', render: (_v, m) => m.samples.join(', ') || '-' },
	{
		title: 'In Tolerance',
		width: 120,
		render: (_v, m) => {
			const verdict = sampleVerdict(m);
			if (verdict === 'ok') return <Tag color="green">In</Tag>;
			if (verdict === 'out') return <Tag color="red">Out</Tag>;
			return <Text type="secondary">-</Text>;
		},
	},
];

function TravelerExpanded({ traveler }: { traveler: TravelerData }) {
	const noCert = traveler.erp?.material_cert_not_available;
	return (
		<Space direction="vertical" style={{ width: '100%' }} size="middle">
			{noCert && (
				<Text type="secondary">
					Cert N/A: material marked as having no mill certificate
					{noCert.by_name || noCert.by ? ` by ${noCert.by_name || noCert.by}` : ''}
					{noCert.at ? ` on ${dayjs(noCert.at).format('MM-DD-YYYY')}` : ''}
				</Text>
			)}
			<div>
				<Text strong>Workflow</Text>
				<Table
					size="small"
					pagination={false}
					rowKey={(r) => r.name}
					dataSource={traveler.workflow}
					columns={[
						{ title: 'Step', dataIndex: 'name' },
						{ title: 'Initials', dataIndex: 'initials' },
						{ title: 'Date', dataIndex: 'date' },
					]}
				/>
			</div>
			<Flex gap="middle" wrap="wrap">
				<div style={{ flex: 1, minWidth: 260 }}>
					<Text strong>Production Outake</Text>
					<Table
						size="small"
						pagination={false}
						rowKey={(_r, i) => `o-${i}`}
						dataSource={traveler.outake}
						columns={[
							{ title: 'Employee', dataIndex: 'employee_initials' },
							{ title: 'Qty Out', dataIndex: 'quantity_out' },
							{ title: 'Date', dataIndex: 'date' },
						]}
					/>
				</div>
				<div style={{ flex: 1, minWidth: 260 }}>
					<Text strong>Intake</Text>
					<Table
						size="small"
						pagination={false}
						rowKey={(_r, i) => `i-${i}`}
						dataSource={traveler.intake}
						columns={[
							{ title: 'Employee', dataIndex: 'employee_initials' },
							{ title: 'Qty In', dataIndex: 'quantity_in' },
							{ title: 'Date', dataIndex: 'date' },
						]}
					/>
				</div>
			</Flex>
		</Space>
	);
}

/** Lazily loads a single document's full payload when its row is expanded. */
function DocumentExpanded({ runId, docId }: { runId: string; docId: string }) {
	const { data, isFetching } = useGetAuditDocumentQuery({ runId, docId });
	if (isFetching) return <Spin size="small" />;
	if (!data) return <Empty description="No detail" />;
	const measurements = data.fai_data?.measurements ?? [];
	const gagesUsed = data.fai_data?.gages_used ?? [];
	return (
		<Space direction="vertical" style={{ width: '100%' }} size="middle">
			{data.failures.length > 0 && (
				<div>
					<Text strong style={{ color: '#cf1322' }}>
						Failures
					</Text>
					<IssueList items={data.failures} kind="failures" />
				</div>
			)}
			{data.lookouts.length > 0 && (
				<div>
					<Text strong style={{ color: '#d48806' }}>
						Lookouts
					</Text>
					<IssueList items={data.lookouts} kind="lookouts" />
				</div>
			)}
			{data.traveler_data ? (
				<TravelerExpanded traveler={data.traveler_data} />
			) : (
				<Empty description="No traveler data" />
			)}
			{measurements.length > 0 && (
				<div>
					<Text strong>FAI / Measurements</Text>
					<Table
						size="small"
						pagination={false}
						rowKey={(m) => String(m.id)}
						dataSource={measurements}
						columns={faiMeasurementColumns}
					/>
				</div>
			)}
			{gagesUsed.length > 0 && (
				<div>
					<Text strong>Gages Used (at time of production)</Text>
					<div style={{ marginTop: 4 }}>
						{gagesUsed.map((g) => {
							const oot = g.calibration_status === 'out_of_calibration';
							const parts = [g.serial];
							if (g.calibration_due_date) parts.push(`due ${g.calibration_due_date}`);
							if (g.has_calibration_certificate === false) parts.push('no cert');
							return (
								<Tag key={g.serial} color={oot ? 'red' : 'green'} style={{ marginBottom: 4 }}>
									{parts.join(' · ')}
								</Tag>
							);
						})}
					</div>
				</div>
			)}
		</Space>
	);
}

const defaultAcceptedRange = (): [Dayjs, Dayjs] => [
	dayjs().subtract(DEFAULT_RANGE_DAYS, 'day'),
	dayjs(),
];

export default function TravelerAudit() {
	const [mode, setMode] = useState<AuditMode>('erp');
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [run, setRun] = useState<AuditRun | null>(null);
	const [statusFilter, setStatusFilter] = useState<AuditStatus | 'all'>('all');
	const [lookoutsOnly, setLookoutsOnly] = useState(false);
	const [failuresOnly, setFailuresOnly] = useState(false);
	const [issuesModal, setIssuesModal] = useState<IssuesModalTarget | null>(null);
	const [reportModalOpen, setReportModalOpen] = useState(false);

	// Triage controls (server-side).
	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [issueCategories, setIssueCategories] = useState<string[]>([]);
	const [ordering, setOrdering] = useState<DocumentOrdering>('severity');

	// Server-side pagination.
	const [travPage, setTravPage] = useState(1);
	const [travPageSize, setTravPageSize] = useState(25);
	const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

	// Debounce the search box so typing doesn't fire a request per keystroke.
	useEffect(() => {
		const t = window.setTimeout(() => {
			setSearch(searchInput.trim());
			setTravPage(1);
		}, 350);
		return () => window.clearTimeout(t);
	}, [searchInput]);

	// Live ERP criteria.
	const [acceptedRange, setAcceptedRange] = useState<[Dayjs | null, Dayjs | null] | null>(
		defaultAcceptedRange()
	);
	const [productionMethod, setProductionMethod] = useState<string>('');
	const [population, setPopulation] = useState<ErpRunPopulation>('awaiting_review');
	const [issuesOnly, setIssuesOnly] = useState(true);
	const [pollingRunId, setPollingRunId] = useState<string | null>(null);
	const [pollSnapshot, setPollSnapshot] = useState<AuditRun | null>(null);
	const [pollTakingLong, setPollTakingLong] = useState(false);
	const pollingRunIdRef = useRef<string | null>(null);
	pollingRunIdRef.current = pollingRunId;

	const [createAuditRun, { isLoading: isRunning }] = useCreateAuditRunMutation();
	const [createErpRun, { isLoading: isQueuingErp }] = useCreateErpRunMutation();
	const [deleteAuditRun, { isLoading: isDeleting }] = useDeleteAuditRunMutation();
	const [triggerExport, { isFetching: isExporting }] = useLazyExportAuditRunQuery();
	const [loadRun] = useLazyGetAuditRunQuery();
	const {
		data: history,
		isFetching: isHistoryLoading,
		refetch: refetchHistory,
	} = useGetAuditRunsQuery();

	const runId = run?.id ?? '';

	const { data: travDocs, isFetching: isTravLoading, refetch: refetchDocs } = useGetAuditRunDocumentsQuery(
		{
			runId,
			status: statusFilter,
			lookoutsOnly,
			failuresOnly,
			search,
			issueCategories,
			ordering,
			page: travPage,
			pageSize: travPageSize,
		},
		{ skip: !run }
	);
	const { data: breakdown } = useGetIssueBreakdownQuery(runId, { skip: !run });

	// Explicit poll with force-refetch. RTK's pollingInterval can keep serving a
	// cached pending header, so the UI never flips even after the worker finishes.
	useEffect(() => {
		if (!pollingRunId) {
			setPollSnapshot(null);
			setPollTakingLong(false);
			return;
		}

		let cancelled = false;
		let timeoutId = 0;
		const startedAt = Date.now();
		let lastHistorySync = '';
		setPollTakingLong(false);

		const finishSuccess = (result: AuditRun) => {
			setRun(result);
			setTravPage(1);
			setPollingRunId(null);
			void refetchHistory();
			const s = result.summary;
			message.success(
				`Reviewed ${result.file_count} line item(s): ${s.fail} fail, ${s.review} review.`
			);
		};

		const finishFailure = (result: AuditRun) => {
			setPollingRunId(null);
			void refetchHistory();
			message.error(result.error || 'The ERP review failed.');
		};

		const scheduleNext = (elapsed: number) => {
			const delay =
				elapsed >= ERP_POLL_WARN_MS
					? ERP_POLL_INTERVAL_SLOW_MS
					: elapsed >= 60 * 1000
						? ERP_POLL_INTERVAL_MED_MS
						: ERP_POLL_INTERVAL_FAST_MS;
			timeoutId = window.setTimeout(() => {
				void tick();
			}, delay);
		};

		const tick = async () => {
			if (cancelled) return;
			const elapsed = Date.now() - startedAt;
			if (elapsed >= ERP_POLL_TIMEOUT_MS) {
				setPollingRunId(null);
				message.error(
					'Live ERP review is still pending after 30 minutes. The worker may be down or overloaded — retry, or check Fastlane worker status.'
				);
				return;
			}
			if (elapsed >= ERP_POLL_WARN_MS) setPollTakingLong(true);

			try {
				// preferCacheValue=false forces a network read of the run header.
				const result = await loadRun(pollingRunId, false).unwrap();
				if (cancelled || pollingRunIdRef.current !== pollingRunId) return;

				setPollSnapshot(result);

				const historyKey = `${result.status}:${result.file_count}`;
				if (historyKey !== lastHistorySync) {
					lastHistorySync = historyKey;
					void refetchHistory();
				}

				if (result.status === 'completed') {
					finishSuccess(result);
					return;
				}
				if (result.status === 'failed') {
					finishFailure(result);
					return;
				}
			} catch {
				// Transient API blip — keep polling.
			}

			scheduleNext(elapsed);
		};

		void tick();
		return () => {
			cancelled = true;
			window.clearTimeout(timeoutId);
		};
	}, [pollingRunId, loadRun, refetchHistory]);

	const erpBusy = isQueuingErp || Boolean(pollingRunId);

	const rangeInvalid =
		!acceptedRange?.[0] ||
		!acceptedRange?.[1] ||
		acceptedRange[1].diff(acceptedRange[0], 'day') > MAX_RANGE_DAYS;

	const handleRunErp = async () => {
		if (!acceptedRange?.[0] || !acceptedRange?.[1]) {
			message.warning('Select an Order Accepted date range (max 1 year).');
			return;
		}
		if (acceptedRange[1].diff(acceptedRange[0], 'day') > MAX_RANGE_DAYS) {
			message.warning('Order Accepted date range cannot exceed 1 year.');
			return;
		}
		const criteria: ErpRunCriteria = {
			issues_only: issuesOnly,
			population,
			accepted_date_from: acceptedRange[0].format('YYYY-MM-DD'),
			accepted_date_to: acceptedRange[1].format('YYYY-MM-DD'),
		};
		if (productionMethod) criteria.production_method = productionMethod;
		try {
			const created = await createErpRun(criteria).unwrap();
			if (created.status === 'failed') {
				message.error(created.error || 'Failed to queue the ERP review.');
				return;
			}
			setRun(null);
			setPollingRunId(created.id);
			message.info('Live ERP review started. Reading the latest data...');
		} catch {
			message.error('Failed to start the ERP review. Check the date range (max 1 year).');
		}
	};

	const handleRun = async () => {
		if (!fileList.length) {
			message.warning('Add one or more PDF files first.');
			return;
		}
		const formData = new FormData();
		fileList.forEach((f) => {
			if (f.originFileObj) formData.append('files', f.originFileObj as Blob, f.name);
		});
		try {
			const result = await createAuditRun(formData).unwrap();
			setRun(result);
			setTravPage(1);
			message.success(`Parsed ${result.file_count} file(s).`);
		} catch (err) {
			message.error('Failed to parse the uploaded files.');
		}
	};

	const handleExport = async () => {
		if (!run) return;
		try {
			const blob = await triggerExport(run.id).unwrap();
			downloadBlob(blob, `traveler_audit_${run.id}.xlsx`);
		} catch {
			message.error('Failed to export the run.');
		}
	};

	const handleLoadHistory = async (id: string) => {
		try {
			const result = await loadRun(id, false).unwrap();
			setRun(result);
			setTravPage(1);
		} catch {
			message.error('Failed to load run.');
		}
	};

	const handleDeleteHistory = (row: AuditRunListItem) => {
		const range = formatHistoryDateRange(row);
		const when = new Date(row.created_time).toLocaleString();
		Modal.confirm({
			title: 'Delete this query from history?',
			content:
				row.source === 'erp'
					? `Remove the Live ERP review from ${when} (Order Accepted ${range}). This cannot be undone.`
					: `Remove the upload review from ${when}. This cannot be undone.`,
			okText: 'Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				try {
					await deleteAuditRun(row.id).unwrap();
					if (run?.id === row.id) {
						setRun(null);
						setPollingRunId(null);
					}
					message.success('Removed from history.');
				} catch {
					message.error('Failed to delete the history entry.');
					throw new Error('delete failed');
				}
			},
		});
	};

	const toggleIssueCategory = (key: string) => {
		setIssueCategories((prev) =>
			prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
		);
		setTravPage(1);
	};

	const handleTableChange = (
		_pagination: TablePaginationConfig,
		_filters: Record<string, unknown>,
		sorter: SorterResult<AuditDocumentListItem> | SorterResult<AuditDocumentListItem>[],
		extra: { action: string }
	) => {
		if (extra.action !== 'sort') return;
		setOrdering(sorterToOrdering(sorter));
		setTravPage(1);
	};

	const orderFor = (key: 'filename' | 'status') => {
		if (key === 'filename') {
			if (ordering === 'filename') return 'ascend' as const;
			if (ordering === '-filename') return 'descend' as const;
			return null;
		}
		if (ordering === 'severity') return 'ascend' as const;
		if (ordering === '-severity') return 'descend' as const;
		return null;
	};

	const travelerColumns: ColumnsType<AuditDocumentListItem> = [
		{
			title: 'File',
			dataIndex: 'filename',
			key: 'filename',
			ellipsis: true,
			sorter: true,
			sortOrder: orderFor('filename'),
		},
		{
			title: 'Type',
			dataIndex: 'document_type',
			render: (t: string) => <Tag>{t}</Tag>,
			width: 90,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (s: AuditStatus) => <StatusTag status={s} />,
			width: 90,
			sorter: true,
			sortOrder: orderFor('status'),
			defaultSortOrder: 'ascend',
		},
		{ title: 'Work Order', render: (_v, d) => workOrderLink(d) },
		{ title: 'Part #', render: (_v, d) => partNumberLink(d) },
		{ title: 'Rev', render: (_v, d) => d.header.revision ?? '-', width: 70 },
		{ title: 'Qty', render: (_v, d) => d.header.quantity ?? '-', width: 60 },
		{ title: 'Due Date', render: (_v, d) => d.header.due_date ?? '-' },
		{
			title: 'Packet dates',
			width: 280,
			render: (_v, d) => <OverlayDates doc={d} />,
		},
		{
			title: 'Failures',
			width: 100,
			align: 'center',
			render: (_v, d) =>
				issueCountCell(d.failures.length, 'volcano', () =>
					setIssuesModal({ doc: d, tab: 'failures' })
				),
		},
		{
			title: 'Lookouts',
			width: 100,
			align: 'center',
			render: (_v, d) =>
				issueCountCell(d.lookouts.length, 'gold', () =>
					setIssuesModal({ doc: d, tab: 'lookouts' })
				),
		},
	];

	const summary = run?.summary;

	return (
		<div style={{ padding: 24, width: '100%' }}>
			<Title level={3}>Traveler Audit</Title>
			<Text type="secondary">
				Review Production Traveler and FAI data for compliance issues. Live ERP
				reads the latest data straight from the system and prefers Quality
				Packets (jobs with ballooning / FAI), falling back to travelers only
				when no packet matches the window. By default it sweeps the jobs still
				awaiting their packet review; switch to Already completed to re-check
				work that was signed off. Upload parses printed Traveler / Quality
				Packet PDFs.
			</Text>

			<Card style={{ marginTop: 16 }}>
				<Segmented<AuditMode>
					value={mode}
					onChange={setMode}
					options={[
						{ label: 'Live ERP (latest)', value: 'erp', icon: <ThunderboltOutlined /> },
						{ label: 'Upload packet', value: 'upload', icon: <InboxOutlined /> },
					]}
				/>

				{mode === 'erp' ? (
					<div style={{ marginTop: 16 }}>
						<Flex gap="middle" wrap="wrap" align="flex-end">
							<div>
								<Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
									Order Accepted date range (required, max 1 year)
								</Text>
								<RangePicker
									value={acceptedRange as never}
									onChange={(v) =>
										setAcceptedRange(v as [Dayjs | null, Dayjs | null] | null)
									}
									allowEmpty={[false, false]}
								/>
							</div>
							<div>
								<Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
									Jobs to review
								</Text>
								<Select
									value={population}
									style={{ width: 230 }}
									onChange={setPopulation}
									options={[
										{
											value: 'awaiting_review',
											label: 'Awaiting review (Delivered In Full)',
										},
										{ value: 'completed', label: 'Already completed' },
										{ value: 'both', label: 'Both' },
									]}
								/>
							</div>
							<div>
								<Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
									Production method
								</Text>
								<Select
									value={productionMethod}
									style={{ width: 160 }}
									onChange={setProductionMethod}
									options={[
										{ value: '', label: 'All methods' },
										{ value: 'Fastlane', label: 'Fastlane' },
										{ value: 'Swiss', label: 'Swiss' },
									]}
								/>
							</div>
							<Checkbox
								checked={issuesOnly}
								onChange={(e) => setIssuesOnly(e.target.checked)}
							>
								Issues only
							</Checkbox>
							<Button
								type="primary"
								icon={<ThunderboltOutlined />}
								onClick={handleRunErp}
								loading={erpBusy}
								disabled={rangeInvalid}
							>
								{erpBusy ? 'Reviewing...' : 'Run Live Review'}
							</Button>
						</Flex>
						{pollingRunId && (
							<Alert
								style={{ marginTop: 16 }}
								type={pollTakingLong ? 'warning' : 'info'}
								showIcon
								icon={<Spin size="small" />}
								message={
									pollTakingLong
										? 'Still reviewing — large windows can take several minutes. Results will load automatically when ready.'
										: 'Reviewing line items against the latest ERP data. This can take a while for wide date ranges.'
								}
								description={
									pollSnapshot &&
									pollSnapshot.status === 'running' &&
									pollSnapshot.file_count > 0
										? `Progress: ${pollSnapshot.file_count.toLocaleString()} line item(s) reviewed so far.`
										: undefined
								}
							/>
						)}
					</div>
				) : (
					<div style={{ marginTop: 16 }}>
						<Dragger
							multiple
							accept=".pdf"
							fileList={fileList}
							beforeUpload={() => false}
							onChange={({ fileList: fl }) => setFileList(fl)}
						>
							<p className="ant-upload-drag-icon">
								<InboxOutlined />
							</p>
							<p className="ant-upload-text">
								Click or drag PDF files here to upload
							</p>
							<p className="ant-upload-hint">
								Travelers (Form 20) and Quality Packets are both supported.
							</p>
						</Dragger>
						<Flex gap="small" style={{ marginTop: 16 }}>
							<Button
								type="primary"
								onClick={handleRun}
								loading={isRunning}
								disabled={!fileList.length}
							>
								{isRunning ? 'Parsing...' : `Run Audit (${fileList.length})`}
							</Button>
							<Button onClick={() => setFileList([])} disabled={isRunning}>
								Clear
							</Button>
						</Flex>
					</div>
				)}
			</Card>

			{run && (
				<Card style={{ marginTop: 16 }}>
					<Flex justify="space-between" align="center" wrap="wrap" gap="small">
						<Space size="middle" wrap>
							<Text strong>Results</Text>
							{summary && (
								<Space>
									<Tag color="green">Pass: {summary.pass}</Tag>
									<Tag color="gold">Review: {summary.review}</Tag>
									<Tag color="red">Fail: {summary.fail}</Tag>
									<Tag color="blue">Complete: {summary.complete ?? 0}</Tag>
									<Tag color="purple">Printed: {summary.printed ?? 0}</Tag>
									<Tag>Internal: {summary.internal ?? 0}</Tag>
								</Space>
							)}
						</Space>
						<Space wrap>
							<Input
								allowClear
								prefix={<SearchOutlined />}
								placeholder="Search work order / part / issue text"
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								style={{ width: 260 }}
							/>
							<Select
								value={statusFilter}
								style={{ width: 140 }}
								onChange={(v) => {
									setStatusFilter(v);
									setTravPage(1);
								}}
								options={[
									{ value: 'all', label: 'All statuses' },
									{ value: 'pass', label: 'Pass' },
									{ value: 'review', label: 'Review' },
									{ value: 'fail', label: 'Fail' },
									{ value: 'complete', label: 'Complete' },
									{ value: 'printed', label: 'Printed' },
									{ value: 'internal', label: 'Internal' },
								]}
							/>
							<Checkbox
								checked={failuresOnly}
								onChange={(e) => {
									setFailuresOnly(e.target.checked);
									setTravPage(1);
								}}
							>
								Failures only
							</Checkbox>
							<Checkbox
								checked={lookoutsOnly}
								onChange={(e) => {
									setLookoutsOnly(e.target.checked);
									setTravPage(1);
								}}
							>
								Lookouts only
							</Checkbox>
							<Button
								icon={<DownloadOutlined />}
								onClick={handleExport}
								loading={isExporting}
							>
								Export .xlsx
							</Button>
						</Space>
					</Flex>

					{breakdown && breakdown.breakdown.length > 0 && (
						<Flex wrap="wrap" gap="small" align="center" style={{ marginTop: 12 }}>
							<Text type="secondary">Issues:</Text>
							{breakdown.breakdown.map((b) => {
								const active = issueCategories.includes(b.key);
								return (
									<Tag
										key={b.key}
										color={active ? 'blue' : 'default'}
										style={{ cursor: 'pointer', userSelect: 'none' }}
										onClick={() => toggleIssueCategory(b.key)}
									>
										{b.label} ({b.count})
									</Tag>
								);
							})}
							{issueCategories.length > 0 && (
								<Button
									type="link"
									size="small"
									onClick={() => {
										setIssueCategories([]);
										setTravPage(1);
									}}
								>
									Clear
								</Button>
							)}
						</Flex>
					)}

					{breakdown && (
						<IssueReportTrigger
							breakdown={breakdown}
							onOpen={() => setReportModalOpen(true)}
						/>
					)}

					{run.source === 'erp' && (
						<ReviewPanel
							runId={runId}
							selected={(travDocs?.results ?? []).filter((d) =>
								selectedRowKeys.includes(d.id)
							)}
							statusFilter={statusFilter}
							lookoutsOnly={lookoutsOnly}
							failuresOnly={failuresOnly}
							search={search}
							issueCategories={issueCategories}
							onDone={(opts) => {
								if (opts?.revealRecovered) {
									setFailuresOnly(false);
									setLookoutsOnly(false);
									setIssueCategories([]);
									setStatusFilter('all');
								}
								setSelectedRowKeys([]);
								setIssuesModal(null);
								void refetchDocs();
								void loadRun(runId, false).then((result) => {
									if ('data' in result && result.data) {
										setRun(result.data);
									}
								});
							}}
						/>
					)}

					{summary?.truncated && (
						<Alert
							type="warning"
							showIcon
							style={{ marginTop: 12 }}
							message={
								run.error ||
								`Only the first ${summary.truncated_at} line items were reviewed. Narrow the date range.`
							}
						/>
					)}

					<Table
						style={{ marginTop: 16 }}
						size="small"
						rowKey="id"
						loading={isTravLoading}
						dataSource={travDocs?.results ?? []}
						columns={travelerColumns}
						rowSelection={
							run.source === 'erp'
								? {
										selectedRowKeys,
										onChange: setSelectedRowKeys,
									}
								: undefined
						}
						onChange={handleTableChange}
						expandable={{
							expandedRowRender: (doc) => (
								<DocumentExpanded runId={runId} docId={doc.id} />
							),
						}}
						pagination={{
							current: travPage,
							pageSize: travPageSize,
							total: travDocs?.count ?? 0,
							showSizeChanger: true,
							pageSizeOptions: PAGE_SIZE_OPTIONS,
							onChange: (p, ps) => {
								setTravPage(p);
								setTravPageSize(ps);
							},
						}}
					/>
				</Card>
			)}

			{run?.source === 'erp' && (
				<>
					<PrintQueue runId={runId} />
					<RecoveryAudit runId={runId} />
				</>
			)}

			<IssuesModal
				target={issuesModal}
				onTabChange={(tab) =>
					setIssuesModal((prev) => (prev ? { ...prev, tab } : prev))
				}
				onClose={() => setIssuesModal(null)}
			/>

			<IssueReportModal
				open={reportModalOpen}
				breakdown={breakdown}
				onClose={() => setReportModalOpen(false)}
				onSelectCategory={(key) => {
					setIssueCategories((prev) => (prev.includes(key) ? prev : [...prev, key]));
					setTravPage(1);
					setReportModalOpen(false);
				}}
			/>

			<Card
				title="History"
				style={{ marginTop: 16 }}
				extra={
					<Button
						type="text"
						size="small"
						icon={<ReloadOutlined spin={isHistoryLoading} />}
						onClick={() => {
							void refetchHistory();
						}}
						aria-label="Refresh history"
					/>
				}
			>
				<Table
					size="small"
					rowKey="id"
					loading={isHistoryLoading}
					dataSource={history ?? []}
					pagination={{ pageSize: 10 }}
					columns={[
						{
							title: 'Date',
							dataIndex: 'created_time',
							render: (v: string) => new Date(v).toLocaleString(),
						},
						{
							title: 'Source',
							dataIndex: 'source',
							width: 90,
							render: (s: string) => (
								<Tag color={s === 'erp' ? 'geekblue' : 'default'}>
									{s === 'erp' ? 'Live ERP' : 'Upload'}
								</Tag>
							),
						},
						{
							title: 'Date range',
							width: 220,
							render: (_v, r) => (
								<Text type={r.source === 'erp' ? undefined : 'secondary'}>
									{formatHistoryDateRange(r)}
								</Text>
							),
						},
						{
							title: 'Jobs',
							width: 150,
							render: (_v, r) => (
								<Text type={r.source === 'erp' ? undefined : 'secondary'}>
									{formatHistoryPopulation(r)}
								</Text>
							),
						},
						{
							title: 'State',
							dataIndex: 'status',
							width: 100,
							render: (s: string) => {
								const color =
									s === 'completed'
										? 'green'
										: s === 'failed'
										? 'red'
										: 'gold';
								return <Tag color={color}>{s}</Tag>;
							},
						},
						{ title: 'Items', dataIndex: 'file_count', width: 70 },
						{
							title: 'Summary',
							render: (_v, r) => (
								<Space>
									<Tag color="green">{r.summary?.pass ?? 0}</Tag>
									<Tag color="gold">{r.summary?.review ?? 0}</Tag>
									<Tag color="red">{r.summary?.fail ?? 0}</Tag>
								</Space>
							),
						},
						{
							title: '',
							width: 140,
							render: (_v, r) => (
								<Space size={4}>
									<Button size="small" onClick={() => handleLoadHistory(r.id)}>
										View
									</Button>
									<Button
										size="small"
										danger
										icon={<DeleteOutlined />}
										loading={isDeleting}
										onClick={() => handleDeleteHistory(r)}
									/>
								</Space>
							),
						},
					]}
				/>
			</Card>
		</div>
	);
}
